"use client";

import { useEffect } from "react";

const XPERT_PROACTIVE_SELECTOR =
  '.xpert_chatbot__proactive-message[role="alertdialog"], .xpert_chatbot__proactive-message[role="dialog"]';

// English fallback for third-party Xpert DOM — brand name, not site chrome.
const XPERT_ALERT_LABEL = "Xpert message";

function hasLabelledByName(labelledBy: string): boolean {
  return labelledBy
    .trim()
    .split(/\s+/)
    .some((id) => document.getElementById(id)?.textContent?.trim());
}

function ensureAccessibleName(element: Element) {
  if (!(element instanceof HTMLElement)) return;

  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy && hasLabelledByName(labelledBy)) return;

  const ariaLabel = element.getAttribute("aria-label")?.trim();
  if (ariaLabel) return;

  const title = element.getAttribute("title")?.trim();
  if (title) return;

  element.setAttribute("aria-label", XPERT_ALERT_LABEL);
}

function patchExisting() {
  document
    .querySelectorAll(XPERT_PROACTIVE_SELECTOR)
    .forEach(ensureAccessibleName);
}

function patchNode(node: Element) {
  if (node.matches(XPERT_PROACTIVE_SELECTOR)) {
    ensureAccessibleName(node);
  }
  node
    .querySelectorAll?.(XPERT_PROACTIVE_SELECTOR)
    .forEach(ensureAccessibleName);
}

/**
 * Third-party Xpert injects proactive alertdialogs without an accessible name.
 * Patch them when they appear so axe aria-dialog-name can pass.
 */
export default function XpertChatbotA11yFix() {
  useEffect(() => {
    patchExisting();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.target instanceof Element
        ) {
          patchNode(mutation.target);
          continue;
        }

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          patchNode(node);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "aria-label", "aria-labelledby"],
    });
    return () => observer.disconnect();
  }, []);

  return null;
}

export { ensureAccessibleName, XPERT_ALERT_LABEL, XPERT_PROACTIVE_SELECTOR };
